import { API_URL } from '../../config';
import { getAuthToken } from './auth';

/**
 * Sends browser errors to Admin Center > Errors (POST /api/client-errors).
 *
 * At most 10 reports per page load, and each distinct message once, so a component
 * that throws on every render cannot flood the server from one tab. Reporting is
 * fire-and-forget: it never throws and never shows the user anything -- a broken
 * reporter must not become a second error on the page.
 */
const MAX_PER_PAGE = 10;
let sent = 0;
const seen = new Set<string>();

export function reportClientError(kind: string, error: unknown, extra: { componentStack?: string; code?: string } = {}) {
  try {
    const err = error instanceof Error ? error : new Error(typeof error === 'string' ? error : 'Unknown error');
    const key = `${kind}|${err.message}`;
    if (sent >= MAX_PER_PAGE || seen.has(key)) return;
    seen.add(key);
    sent += 1;
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = getAuthToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    fetch(`${API_URL}/api/client-errors`, {
      method: 'POST',
      headers,
      keepalive: true,
      body: JSON.stringify({
        kind,
        name: err.name,
        message: err.message,
        stack: err.stack,
        url: window.location.href.split('?')[0],
        component_stack: extra.componentStack,
        code: extra.code,
      }),
    }).catch(() => {});
  } catch {
    // Nothing: see the comment above.
  }
}

/** Catches what no code handled: uncaught errors and rejected promises nobody awaited. */
export function installGlobalErrorReporting() {
  window.addEventListener('error', (event) => {
    // A failed <img> or <script> load also fires 'error', with no error object; those
    // are not bugs in our code.
    if (event.error) reportClientError('window', event.error);
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    // An aborted fetch (navigating away mid-request) is not a failure.
    if (reason && reason.name === 'AbortError') return;
    reportClientError('promise', reason);
  });
}

/** For tests. */
export function resetErrorReporterForTests() {
  sent = 0;
  seen.clear();
}
